import {
  BaseBotFormValues,
  BotFormSetters,
  EntryGraceBaseline,
  OverrideType,
  ToggleField,
} from "@shared/features/bots/BotForm/common/types";
import { useContext } from "react";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import {
  ALL_OVERRIDES_TOGGLE_FIELDS,
  CANCEL_ON_PROFIT_BEFORE_ENTRY_TITLE,
  channelValue,
  ENTRY_STRATEGY_TOGGLE_FIELDS,
  MARGIN_GLOBAL_SETTINGS_KEY,
  multiToggleValue,
  STOP_TIMEOUT_TITLE,
  DYNAMIC_TAKE_PROFITS_TITLE,
  TOGGLE_FIELD_TO_STRATEGY_FIELDS,
  WITHOUT_CHANNEL_TOGGLE_FIELDS,
  WITHOUT_OFF_TOGGLE_FIELDS,
} from "@shared/features/bots/BotForm/common/constants";
import { isSymbol } from "@shared/types/utils";
import {
  AmountType,
  BotType,
  LanguageNameSpaces,
  UnifiedStrategyData,
} from "@shared/types";
import { get, pickBy } from "lodash";
import {
  useOverrideFormValue,
  useToggleFieldParent,
} from "@shared/features/bots/BotForm/common/hooks";
import useFormHelperData from "@shared/features/bots/BotForm/common/hooks/useFormHelperData";
import { getHasCustomSymbols } from "@shared/features/bots/utils";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { TranslationKeys } from "@shared/common/i18n/types";

interface useTitleWithToggleProps {
  fieldType?: ToggleField;
  hideWithOpacity?: boolean;
  overrideObjectName?: string;
  isMultiValue?: boolean;
  getDefaultValue?: () => any;
  onPersonalClick?: () => void;
  onOverrideChange?: (overrideType?: OverrideType) => void;
  defaultChannelValue?: any;
}

export const useTitleWithToggle = ({
  fieldType,
  hideWithOpacity,
  overrideObjectName: propsOverrideObjectName,
  isMultiValue: propsIsMultiValue,
  getDefaultValue,
  onPersonalClick,
  onOverrideChange,
  defaultChannelValue,
}: useTitleWithToggleProps) => {
  const {
    toggleField,
    checkedFields,
    formValues,
    botType,
    formSetters,
    setEntryStrategy,
    setDefaultTakeProfit,
    setAmountValue,
    setAmountType,
    setTakeProfitStrategy,
    setHedgingMode,
    isChannelConfiguration,
    addFieldToInitialize,
    entryStrategy,
    hedgingMode,
    amountType,
    amountValue,
    takeProfitStrategy,
    setEntryGracePercent,
    setEntryGraceBaseline,
    entryGracePercent,
    entryGraceBaseline,
    multiSymbolMarginData,
  } = useContext(BotFormContext)!;
  const isSignalBot = botType === BotType.SIGNAL;
  const isGridBot = botType === BotType.GRID;
  const isHedgingBot = botType === BotType.HEDGING;
  const isTradingViewSignalsBot = botType === BotType.TRADING_VIEW_SIGNALS;
  const isParentChecked = useToggleFieldParent(fieldType, true);
  const { getHelperData } = useFormHelperData();
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);

  const getFieldValue = () => {
    if (!fieldType) return;
    if (
      isSignalBot &&
      fieldType === ToggleField.TAKE_PROFITS &&
      !isSymbol(formValues.defaultTakeProfit)
    ) {
      return formValues.defaultTakeProfit?.percent;
    } else if (isHedgingBot && fieldType === ToggleField.ENTRIES) {
      return hedgingMode;
    } else if (fieldType in TOGGLE_FIELD_TO_STRATEGY_FIELDS) {
      const strategyFields = TOGGLE_FIELD_TO_STRATEGY_FIELDS[fieldType];
      const strategy = ENTRY_STRATEGY_TOGGLE_FIELDS.has(fieldType)
        ? entryStrategy
        : takeProfitStrategy;
      if (strategy && strategyFields && !isSymbol(strategy)) {
        if (strategyFields.length > 1) {
          const value = pickBy(strategy, (_, key) =>
            strategyFields.includes(key as any)
          );
          const hasChannelValues = Object.values(value).some(
            (objectValue) => objectValue === channelValue
          );
          return hasChannelValues ? channelValue : value;
        } else if (strategyFields.length)
          return (strategy as UnifiedStrategyData)?.[strategyFields[0]];
      }
    } else if (fieldType === ToggleField.AMOUNT_PER_TRADE) {
      if (amountType === multiToggleValue) return multiToggleValue;
      return amountType
        ? {
            baseline: amountType,
            percent: amountValue,
          }
        : undefined;
    } else if (fieldType === ToggleField.ENTRY_GRACE_PERCENT) {
      return entryGracePercent && entryGraceBaseline
        ? {
            entry_grace_percent: entryGracePercent,
            entry_grace_baseline: entryGraceBaseline,
          }
        : undefined;
    } else if (fieldType === ToggleField.MARGIN) {
      const parsedMultiSymbolMarginData =
        multiSymbolMarginData && !isSymbol(multiSymbolMarginData)
          ? multiSymbolMarginData
          : {};
      const globalSettings =
        parsedMultiSymbolMarginData?.[MARGIN_GLOBAL_SETTINGS_KEY];
      const globalSettingsIsChannel =
        !globalSettings ||
        Object.values(globalSettings).every(
          (value) => !value || isSymbol(value)
        );
      if (
        !getHasCustomSymbols(parsedMultiSymbolMarginData) &&
        globalSettingsIsChannel
      ) {
        return channelValue;
      }
      return multiSymbolMarginData;
    }
    const getter = TOGGLE_FIELD_TO_GETTER[fieldType];
    if (getter) {
      return get(formValues, getter);
    }
  };

  const getSignalsBotStrategySetter = () => {
    if (!fieldType) return;
    const isEntry = ENTRY_STRATEGY_TOGGLE_FIELDS.has(fieldType);
    const setFunction = isEntry ? setEntryStrategy : setTakeProfitStrategy;
    return (value: any) => {
      addFieldToInitialize?.(isEntry ? "entryStrategy" : "takeProfitStrategy");
      // @ts-ignore
      setFunction((strategy) => {
        const updatedStrategy = { ...strategy };
        const fieldsToSet = TOGGLE_FIELD_TO_STRATEGY_FIELDS[fieldType];
        fieldsToSet?.forEach((fieldKey) => {
          if (fieldsToSet.length === 1 || isSymbol(value)) {
            updatedStrategy[fieldKey] = value;
          } else {
            updatedStrategy[fieldKey] = value?.[fieldKey];
          }
        });
        return updatedStrategy;
      });
    };
  };

  const getFieldSetter = () => {
    if (!fieldType) return;
    const setterName = TOGGLE_FIELD_TO_SETTER[fieldType];
    if (isHedgingBot && fieldType === ToggleField.ENTRIES) {
      return setHedgingMode;
    }
    if (fieldType === ToggleField.AMOUNT_PER_TRADE) {
      return (
        value:
          | {
              baseline: AmountType;
              percent: number;
            }
          | undefined
      ) => {
        addFieldToInitialize?.("amountValue");
        setAmountValue(value?.percent);
        setAmountType(value?.baseline);
      };
    }
    if (fieldType === ToggleField.ENTRY_GRACE_PERCENT) {
      return (
        value:
          | {
              entry_grace_percent: number;
              entry_grace_baseline: EntryGraceBaseline;
            }
          | undefined
      ) => {
        setEntryGracePercent(value?.entry_grace_percent);
        setEntryGraceBaseline(value?.entry_grace_baseline);
      };
    }
    if (isSignalBot && fieldType === ToggleField.TAKE_PROFITS) {
      return (value: any) => {
        setDefaultTakeProfit((previousValue) => ({
          // @ts-ignore
          ...previousValue,
          percent: value,
        }));
      };
    }
    if (setterName && setterName in formSetters) {
      return formSetters[setterName];
    } else if (fieldType in TOGGLE_FIELD_TO_STRATEGY_FIELDS) {
      return getSignalsBotStrategySetter();
    }
  };

  const fieldValue = getFieldValue();
  const setFunction = getFieldSetter();
  const isChannelValue = fieldValue === channelValue;
  const checked = !fieldType || checkedFields.has(fieldType);
  const isMultiValue =
    propsIsMultiValue ?? (!isChannelValue && isSymbol(fieldValue));

  const { onOverrideTypeChange } = useOverrideFormValue({
    currentValue: fieldValue,
    getDefaultValue,
    setFunction,
    toggleFieldType: fieldType,
    onPersonalClick,
    onOverrideChange,
    defaultChannelValue,
  });

  const getOverrideType = () => {
    if (!checked)
      return (isChannelConfiguration || isTradingViewSignalsBot) &&
        fieldType !== ToggleField.CLOSE_ON_TP_BEFORE_ENTRY
        ? OverrideType.USER_CONFIGURATION
        : OverrideType.OFF;
    else if (
      fieldType &&
      [
        ToggleField.ENTRY_NUM_ACTIVE_ORDERS,
        ToggleField.TAKE_PROFIT_NUM_ACTIVE_ORDERS,
      ].includes(fieldType)
    ) {
      return isChannelValue ? OverrideType.ALL : OverrideType.CUSTOM;
    } else if (isChannelValue) {
      if (
        fieldType === ToggleField.CLOSE_ON_TP_BEFORE_ENTRY &&
        isChannelConfiguration
      ) {
        return OverrideType.DYNAMIC;
      }
      return OverrideType.CHANNEL;
    }
    return OverrideType.PERSONAL;
  };

  const getOverrideOptions = () => {
    if (!fieldType) return;
    if (fieldType === ToggleField.CLOSE_ON_TP_BEFORE_ENTRY) {
      return [
        OverrideType.OFF,
        isChannelConfiguration ? OverrideType.DYNAMIC : OverrideType.CHANNEL,
        OverrideType.PERSONAL,
      ];
    }
    if (isChannelConfiguration || isTradingViewSignalsBot) {
      return [OverrideType.USER_CONFIGURATION, OverrideType.PERSONAL];
    }
    if (isSignalBot) {
      if (
        [
          ToggleField.ENTRY_NUM_ACTIVE_ORDERS,
          ToggleField.TAKE_PROFIT_NUM_ACTIVE_ORDERS,
        ].includes(fieldType)
      ) {
        return [OverrideType.ALL, OverrideType.CUSTOM];
      }
      if (ALL_OVERRIDES_TOGGLE_FIELDS.has(fieldType))
        return [OverrideType.OFF, OverrideType.CHANNEL, OverrideType.PERSONAL];
      if (WITHOUT_OFF_TOGGLE_FIELDS.has(fieldType))
        return [OverrideType.CHANNEL, OverrideType.PERSONAL];
      if (WITHOUT_CHANNEL_TOGGLE_FIELDS.has(fieldType))
        return [OverrideType.OFF, OverrideType.PERSONAL];
    }
  };

  const overrideType = getOverrideType();
  const overrideOptions = getOverrideOptions();

  const isLargeChannelValueWarning =
    !!fieldType &&
    [
      ToggleField.ENTRY_RATIOS,
      ToggleField.TAKE_PROFIT_RATIOS,
      ToggleField.STOP,
    ].includes(fieldType);
  const hideContentWithOpacity =
    isParentChecked && (overrideType !== OverrideType.PERSONAL || isMultiValue);
  let baseOpacity: number | undefined;
  if (isMultiValue) baseOpacity = 0.2;
  else if (isChannelValue) baseOpacity = 0.15;
  const showOverrideButton = isParentChecked && isMultiValue && !isChannelValue;
  const showOverrideTypeToggle = !!overrideOptions;
  const isOpacityWrapper =
    hideWithOpacity ||
    ((isMultiValue || isChannelValue) &&
      checked &&
      (!isSignalBot || isLargeChannelValueWarning));

  const overrideObjectName =
    propsOverrideObjectName ??
    (fieldType &&
      t(
        TOGGLE_FIELD_TO_OVERRIDE_OBJECT_NAME[
          fieldType
        ] as TranslationKeys<LanguageNameSpaces.BOTS>
      ));
  const overrideButtonText = t("buttons.override", {
    overrideObjectName: overrideObjectName || t("labels.settings"),
  });
  const channelOverrideDescription = t(
    "messages.info.channel_override_description",
    { overrideObjectName }
  );

  const onToggleChange = (checked: boolean) => {
    fieldType && toggleField(fieldType, checked);
  };

  const overrideTypeToggleData = {
    shouldDisplay: showOverrideTypeToggle,
    onChange: onOverrideTypeChange,
    value: overrideType,
    options: overrideOptions,
    showPersonalAsOn:
      (isGridBot && fieldType === ToggleField.STOP) ||
      (isSignalBot &&
        (fieldType === ToggleField.CLOSE_ON_TP_BEFORE_ENTRY ||
          fieldType === ToggleField.SKIP_WITHOUT_STOP_LOSS)) ||
      fieldType === ToggleField.TAKE_PROFIT_DYNAMIC ||
      fieldType === ToggleField.ALTERNATIVE_USD_PAIRS ||
      isChannelConfiguration ||
      isTradingViewSignalsBot,
  };

  const hideToggle = isMultiValue && !isParentChecked;
  const expanded = checked && !isChannelValue;

  return {
    checked,
    expanded,
    hideContentWithOpacity,
    baseOpacity,
    isOpacityWrapper,
    overrideButtonText,
    onToggleChange,
    showOverrideButton,
    overrideTypeToggleData,
    channelOverrideDescription,
    hideToggle,
    isLargeChannelValueWarning,
    helperData: getHelperData(fieldType),
  };
};

const TOGGLE_FIELD_TO_GETTER: {
  [key in ToggleField]?: keyof BaseBotFormValues | string;
} = {
  [ToggleField.ENTRIES]: "entryStrategy",
  [ToggleField.TAKE_PROFITS]: "defaultTakeProfit",
  [ToggleField.TAKE_PROFIT_STRATEGY]: "takeProfitStrategy",
  [ToggleField.TRAILING_TAKE_PROFIT]: "trailingTakeProfit",
  [ToggleField.TRAILING_ENTRY]: "trailingEntry",
  [ToggleField.STOP]: "defaultStop",
  [ToggleField.TRAILING_STOP]: "trailingStopData",
  [ToggleField.STOP_TIMEOUT]: "stopTimeout",
  [ToggleField.ENTRY_GRACE_PERCENT]: "entryGracePercent",
  [ToggleField.MARGIN]: "multiSymbolMarginData",
  [ToggleField.CLOSE_ON_TP_BEFORE_ENTRY]: "cancelOnProfitBeforeEntry",
  [ToggleField.SKIP_WITHOUT_STOP_LOSS]: "skipWithoutStopLoss",
  [ToggleField.TAKE_PROFIT_DYNAMIC]: "defaultTakeProfit.baseline",
  [ToggleField.FIRST_ENTRY]: "entryStrategy.first_order_percentage",
  [ToggleField.ENTRY_PRICE_STRATEGY]: "entryStrategy.price_scale",
  [ToggleField.TAKE_PROFIT_PRICE_STRATEGY]: "takeProfitStrategy.price_scale",
  [ToggleField.ENTRY_RATIOS]: "entryStrategy.strategy_style",
  [ToggleField.TAKE_PROFIT_RATIOS]: "takeProfitStrategy.strategy_style",
  [ToggleField.ENTRY_NUM_ORDERS]: "entryStrategy.number_of_orders",
  [ToggleField.TAKE_PROFIT_NUM_ORDERS]: "takeProfitStrategy.number_of_orders",
  [ToggleField.ENTRY_NUM_ACTIVE_ORDERS]:
    "entryStrategy.number_of_active_orders",
  [ToggleField.TAKE_PROFIT_NUM_ACTIVE_ORDERS]:
    "takeProfitStrategy.number_of_active_orders",
  [ToggleField.TAKE_PROFIT_GRACE_PERCENT]: "takeProfitGracePercent",
  [ToggleField.OPERATION_HOURS]: "operationHoursEvents",
};
const TOGGLE_FIELD_TO_SETTER: {
  [key in ToggleField]?: keyof BotFormSetters;
} = {
  [ToggleField.TAKE_PROFITS]: "setDefaultTakeProfit",
  [ToggleField.TAKE_PROFIT_STRATEGY]: "setTakeProfitStrategy",
  [ToggleField.ENTRIES]: "setEntryStrategy",
  [ToggleField.TRAILING_STOP]: "setTrailingStopData",
  [ToggleField.TRAILING_ENTRY]: "setTrailingEntry",
  [ToggleField.TRAILING_TAKE_PROFIT]: "setTrailingTakeProfit",
  [ToggleField.STOP]: "setDefaultStop",
  [ToggleField.STOP_TIMEOUT]: "setStopTimeout",
  [ToggleField.CLOSE_ON_TP_BEFORE_ENTRY]: "setCancelOnProfitBeforeEntry",
  [ToggleField.MARGIN]: "setMultiSymbolMarginData",
  [ToggleField.TAKE_PROFIT_GRACE_PERCENT]: "setTakeProfitGracePercent",
  [ToggleField.OPERATION_HOURS]: "setOperationHoursEvents",
};

const TOGGLE_FIELD_TO_OVERRIDE_OBJECT_NAME: {
  [key in ToggleField]?: TranslationKeys<LanguageNameSpaces.BOTS>;
} = {
  [ToggleField.TAKE_PROFIT_STRATEGY]: "labels.take_profit_strategy",
  [ToggleField.ENTRIES]: "labels.entry_strategy",
  [ToggleField.TRAILING_STOP]: "labels.trailing_stop",
  [ToggleField.TRAILING_ENTRY]: "labels.trailing_entry",
  [ToggleField.TRAILING_TAKE_PROFIT]: "labels.trailing_take_profit",
  [ToggleField.FIRST_ENTRY]: "labels.first_entry_amount",
  [ToggleField.STOP]: "labels.default_stop",
  [ToggleField.STOP_TIMEOUT]: STOP_TIMEOUT_TITLE,
  [ToggleField.CLOSE_ON_TP_BEFORE_ENTRY]: CANCEL_ON_PROFIT_BEFORE_ENTRY_TITLE,
  [ToggleField.TAKE_PROFIT_DYNAMIC]: DYNAMIC_TAKE_PROFITS_TITLE,
  [ToggleField.MARGIN]: "labels.leverage",
  [ToggleField.AMOUNT_PER_TRADE]: "labels.amount_per_trade",
};
